import * as React from 'react';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Drawer, DrawerContent, DrawerTrigger } from '@/components/ui/drawer';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Check } from 'lucide-react';

export interface ComboboxItem {
  value: string;
  label: string;
  [key: string]: any; // Allow additional properties
}

interface ResponsiveComboboxProps {
  items: ComboboxItem[];
  value?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  className?: string;
  buttonClassName?: string;
  contentClassName?: string;
  disabled?: boolean;
  'data-testid'?: string;
}

export function ResponsiveCombobox({
  items,
  value,
  onValueChange,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  emptyText = 'No results found.',
  className,
  buttonClassName,
  contentClassName,
  disabled = false,
  'data-testid': testId,
}: ResponsiveComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const isMobile = useIsMobile();
  const isDesktop = !isMobile;

  const selectedItem = items.find((item) => item.value === value);

  const handleSelect = (selectedValue: string) => {
    onValueChange?.(selectedValue);
    setOpen(false);
  };

  const trigger = (
    <Button
      variant="outline"
      role="combobox"
      aria-expanded={open}
      aria-label={placeholder}
      className={cn('w-full justify-between', buttonClassName)}
      disabled={disabled}
      data-testid={testId}
    >
      <span className="truncate">
        {selectedItem ? selectedItem.label : placeholder}
      </span>
    </Button>
  );

  const content = (
    <ComboboxList
      items={items}
      value={value}
      onSelect={handleSelect}
      searchPlaceholder={searchPlaceholder}
      emptyText={emptyText}
    />
  );

  if (isDesktop) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild className={className}>
          {trigger}
        </PopoverTrigger>
        <PopoverContent
          className={cn('w-full p-0', contentClassName)}
          align="start"
          sideOffset={4}
        >
          {content}
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild className={className}>
        {trigger}
      </DrawerTrigger>
      <DrawerContent className={contentClassName}>
        <div className="mt-4 border-t">{content}</div>
      </DrawerContent>
    </Drawer>
  );
}

interface ComboboxListProps {
  items: ComboboxItem[];
  value?: string;
  onSelect: (value: string) => void;
  searchPlaceholder: string;
  emptyText: string;
}

function ComboboxList({
  items,
  value,
  onSelect,
  searchPlaceholder,
  emptyText,
}: ComboboxListProps) {
  return (
    <Command className="[&_[cmdk-input]]:focus:outline-none [&_[cmdk-input]]:focus:ring-0">
      <CommandInput placeholder={searchPlaceholder} />
      <CommandList className="overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-border [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb:hover]:bg-muted-foreground/50">
        <CommandEmpty>{emptyText}</CommandEmpty>
        <CommandGroup>
          {items.map((item) => (
            <CommandItem
              key={item.value}
              value={item.label}
              onSelect={() => onSelect(item.value)}
              data-testid={`combobox-item-${item.value}`}
            >
              <Check
                className={cn(
                  'mr-2 h-4 w-4',
                  value === item.value ? 'opacity-100' : 'opacity-0'
                )}
              />
              {item.label}
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}
